import React, { useEffect, useState, useMemo } from "react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WO_STATUSES, URGENCIES } from "@/lib/constants";
import { Loader2, Pencil, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export function EditWorkOrderDialog({ wo, invoiceExists, onDone }) {
  const { user } = useAuth();
  const canEditFinancials = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [props, setProps] = useState([]);
  const [clientQuery, setClientQuery] = useState("");
  const [f, setF] = useState({});

  const initForm = () => ({
    client_id: wo.client_id || "",
    property_id: wo.property_id || "",
    service_category: wo.service_category || "",
    job_title: wo.job_title || "",
    problem_description: wo.problem_description || "",
    urgency: wo.urgency || "Routine",
    customer_price: wo.customer_price ?? "",
    contractor_payout: wo.contractor_payout ?? "",
    material_cost: wo.material_cost ?? "",
    requested_date: wo.requested_date || "",
    scheduled_date: wo.scheduled_date || "",
    scheduled_time: wo.scheduled_time || "",
    due_date: wo.due_date || "",
    assigned_contractor_id: wo.assigned_contractor_id || "unassigned",
    status: wo.status || "New",
    internal_notes: wo.internal_notes || "",
  });

  useEffect(() => {
    if (open) {
      setF(initForm());
      setClientQuery("");
      api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {});
      api.get("/contractors").then(({ data }) => setContractors(data)).catch(() => setContractors([]));
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (open && f.client_id) {
      api.get(`/properties?client_id=${f.client_id}`).then(({ data }) => setProps(data)).catch(() => setProps([]));
    }
    // eslint-disable-next-line
  }, [open, f.client_id]);

  const filteredClients = useMemo(
    () => clients.filter((c) => (c.name + c.email).toLowerCase().includes(clientQuery.toLowerCase())),
    [clients, clientQuery]
  );
  const gp = (Number(f.customer_price) || 0) - (Number(f.contractor_payout) || 0) - (Number(f.material_cost) || 0);
  const gm = Number(f.customer_price) ? (gp / Number(f.customer_price)) * 100 : 0;
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!f.property_id) return toast.error("Select a property");
    if (!f.service_category || !f.problem_description) return toast.error("Trade/Service and Description are required");
    setBusy(true);
    try {
      const payload = {
        property_id: f.property_id, service_category: f.service_category, job_title: f.job_title,
        problem_description: f.problem_description, urgency: f.urgency, status: f.status,
        requested_date: f.requested_date, scheduled_date: f.scheduled_date, scheduled_time: f.scheduled_time,
        due_date: f.due_date, internal_notes: f.internal_notes,
        assigned_contractor_id: f.assigned_contractor_id === "unassigned" ? "" : f.assigned_contractor_id,
      };
      if (canEditFinancials) {
        payload.customer_price = Number(f.customer_price) || 0;
        payload.contractor_payout = Number(f.contractor_payout) || 0;
        payload.material_cost = Number(f.material_cost) || 0;
      }
      await api.put(`/work-orders/${wo.id}`, payload);
      toast.success("Work order updated successfully.");
      setOpen(false);
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update work order"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="edit-wo-btn" variant="outline" className="w-full border-blue-200 text-blue-700 hover:bg-blue-50">
          <Pencil className="w-4 h-4 mr-1" /> Edit Work Order
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-wo-modal">
        <DialogHeader>
          <DialogTitle>Edit Work Order {wo.wo_number}</DialogTitle>
          <DialogDescription>Update client, property, job, scheduling, assignment, status and financials.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Client</Label>
            <Input data-testid="edit-wo-client-search" value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} placeholder="Search clients…" className="mt-1.5 mb-2" />
            <Select value={f.client_id} onValueChange={(v) => setF({ ...f, client_id: v, property_id: "" })}>
              <SelectTrigger data-testid="edit-wo-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
              <SelectContent>{filteredClients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Property</Label>
            <Select value={f.property_id} onValueChange={(v) => setF({ ...f, property_id: v })}>
              <SelectTrigger data-testid="edit-wo-property-select" className="mt-1.5"><SelectValue placeholder={props.length ? "Select property" : "No properties for this client"} /></SelectTrigger>
              <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.street_address}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Trade / Service</Label><Input data-testid="edit-wo-service" value={f.service_category} onChange={set("service_category")} className="mt-1.5" placeholder="Plumbing, HVAC…" /></div>
            <div><Label>Job Title</Label><Input data-testid="edit-wo-title" value={f.job_title} onChange={set("job_title")} className="mt-1.5" /></div>
          </div>
          <div><Label>Description</Label><Textarea data-testid="edit-wo-desc" value={f.problem_description} onChange={set("problem_description")} className="mt-1.5" rows={2} /></div>
          <div><Label>Urgency</Label>
            <Select value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })}><SelectTrigger data-testid="edit-wo-urgency" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{URGENCIES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>

          {canEditFinancials && (
            <div className="space-y-2">
              {invoiceExists && (
                <div className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="edit-wo-invoice-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>This work order already has an invoice. Updating pricing will not automatically update the existing invoice.</span>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Client Price</Label><Input data-testid="edit-wo-price" type="number" value={f.customer_price} onChange={set("customer_price")} className="mt-1.5" /></div>
                <div><Label>Contractor Cost</Label><Input data-testid="edit-wo-payout" type="number" value={f.contractor_payout} onChange={set("contractor_payout")} className="mt-1.5" /></div>
                <div><Label>Material Cost</Label><Input data-testid="edit-wo-material" type="number" value={f.material_cost} onChange={set("material_cost")} className="mt-1.5" /></div>
              </div>
              <div className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2" data-testid="edit-wo-gp">Est. Gross Profit: <b>{money(gp)}</b> · Margin: <b>{gm.toFixed(1)}%</b></div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Requested Date</Label><Input data-testid="edit-wo-requested" type="date" value={f.requested_date} onChange={set("requested_date")} className="mt-1.5" /></div>
            <div><Label>Scheduled Date</Label><Input data-testid="edit-wo-scheduled" type="date" value={f.scheduled_date} onChange={set("scheduled_date")} className="mt-1.5" /></div>
            <div><Label>Scheduled Time</Label><Input data-testid="edit-wo-time" type="time" value={f.scheduled_time} onChange={set("scheduled_time")} className="mt-1.5" /></div>
            <div><Label>Due Date</Label><Input data-testid="edit-wo-due" type="date" value={f.due_date} onChange={set("due_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Assigned Contractor</Label>
              <Select value={f.assigned_contractor_id} onValueChange={(v) => setF({ ...f, assigned_contractor_id: v })}><SelectTrigger data-testid="edit-wo-contractor" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger data-testid="edit-wo-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-64">{WO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Internal Notes</Label><Textarea data-testid="edit-wo-notes" value={f.internal_notes} onChange={set("internal_notes")} className="mt-1.5" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="edit-wo-submit" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
